import { useReactiveVar } from '@apollo/client';
import { find } from 'lodash';
import React, { useEffect, useState } from 'react';
import { userPrivileges } from '../../../apollo/cache';
import { getCsvData } from '../reportDownloads/aggregateAppraisalReport';
import { getDetailedCsvData } from '../reportDownloads/detailedAppraisalReport';
import { getActualPerformanceReportCsvData } from '../reportDownloads/actualPerformanceReport';
import { getDeltaPerformanceReportCsvData } from '../reportDownloads/deltaPerformanceReport';
import { saveAs } from 'file-saver';
import PrivilegeNames from '../../../constants/privilegeNames';
import { addFilesToZip } from '../services/addFilesToZipService';
import DownloadModalWithButton from '@shared/components/DownloadModalWithButton';

const getAvailableReports = (
  privileges,
  hasSharedDetailedAppraisalData,
  hasSharedPerformanceData
) => {
  if (
    (!privileges || privileges.length === 0) &&
    !hasSharedDetailedAppraisalData &&
    !hasSharedPerformanceData
  )
    return [];

  const reports = [];

  if (
    privileges.includes(PrivilegeNames.DetailedAppraisalReport) ||
    hasSharedDetailedAppraisalData
  ) {
    reports.push({
      name: 'Detailed Appraisal Report'
    });
  }

  if (privileges.includes(PrivilegeNames.AggregateAppraisalReport)) {
    reports.push({
      name: 'Aggregate Appraisal Report'
    });
  }

  if (privileges.includes(PrivilegeNames.ActualPerformanceReport) || hasSharedPerformanceData) {
    reports.push({
      name: 'Actual Performance Report'
    });
  }

  if (privileges.includes(PrivilegeNames.DeltaPerformanceReport)) {
    reports.push({
      name: 'Delta Performance Report'
    });
  }

  return reports;
};

/**
 * @typedef {object} DownloadReportModalProps
 * @property {boolean} [hasSharedDetailedAppraisalData]
 * @property {boolean} [hasSharedPerformanceData]
 * @property {number} orgUnitId
 */

/**
 * @param {DownloadReportModalProps} props
 */
const DownloadReportModal = props => {
  const {
    orgUnitId,
    hasSharedDetailedAppraisalData = false,
    hasSharedPerformanceData = false
  } = props;

  const [reportsData, setReportsData] = useState([]);
  const privileges = useReactiveVar(userPrivileges);

  useEffect(() => {
    if (
      (privileges && privileges.length > 0) ||
      hasSharedDetailedAppraisalData ||
      hasSharedPerformanceData
    ) {
      if (
        privileges.includes(PrivilegeNames.DetailedAppraisalReport) ||
        hasSharedDetailedAppraisalData
      ) {
        getDetailedCsvData(orgUnitId, hasSharedDetailedAppraisalData).then(data => {
          setReportsData(reports => [
            ...reports,
            {
              name: 'Detailed Appraisal Report',
              filename: 'detailed-appraisal-report',
              disabled: !data?.length,
              reportData: data
            }
          ]);
        });
      }

      if (privileges.includes(PrivilegeNames.AggregateAppraisalReport)) {
        getCsvData(orgUnitId).then(data => {
          setReportsData(reports => [
            ...reports,
            {
              name: 'Aggregate Appraisal Report',
              filename: 'aggregate-appraisal-report',
              disabled: !data || data.length == 0,
              reportData: data
            }
          ]);
        });
      }

      if (privileges.includes(PrivilegeNames.ActualPerformanceReport) || hasSharedPerformanceData) {
        getActualPerformanceReportCsvData(orgUnitId, hasSharedPerformanceData).then(data => {
          setReportsData(reports => [
            ...reports,
            {
              name: 'Actual Performance Report',
              filename: 'actual-performance-report',
              disabled: !data || data.length == 0,
              reportData: data
            }
          ]);
        });
      }

      if (privileges.includes(PrivilegeNames.DeltaPerformanceReport)) {
        getDeltaPerformanceReportCsvData(orgUnitId).then(data => {
          setReportsData(reports => [
            ...reports,
            {
              name: 'Delta Performance Report',
              filename: 'delta-performance-report',
              disabled: !data || data.length == 0,
              reportData: data
            }
          ]);
        });
      }
    }
  }, [privileges, hasSharedDetailedAppraisalData, hasSharedPerformanceData, orgUnitId]);

  const downloadReports = async selectedReports => {
    const zip = require('jszip')();
    const AVAILABLE_REPORTS = reportsData;
    for (const report of selectedReports) {
      const reportDefinition = find(AVAILABLE_REPORTS, r => r.name === report);
      addFilesToZip(zip, reportDefinition);
    }

    zip.generateAsync({ type: 'blob' }).then(function (content) {
      // save file as Zip
      saveAs(content, 'OrganizationReports.zip');
    });
  };

  const availableReports = getAvailableReports(
    privileges,
    hasSharedDetailedAppraisalData,
    hasSharedPerformanceData
  );

  const reports = availableReports.map(report => {
    const reportData = reportsData.find(x => x.name === report.name);

    return {
      title: report.name,
      disabled: reportData ? !reportData.reportData.length : true,
      value: report.name
    };
  });

  return <DownloadModalWithButton reports={reports} downloadReports={downloadReports} />;
};

export default DownloadReportModal;
